import {DocumentActionTypes, StageActionTypes} from "../Actions/actionTypes";
import {
    isExclusiveSystemCriterion,
    normalizeExclusiveSystemSelection,
} from "../utils/globalAnalysisCriterion";
import { normalizeLabelsVariant } from "../utils/renderingLabels";

const initialStageState = {
    criteriaListe: [],
    templates: [],
    selectedTemplate: null,
    selectedCriteria: [
        // {id:"",reference_file_ids:[]}
    ],
    loading: false,
    error: null,

    // Analyse data
    id: null,
    type_analyse: null,
    title: "",
    context: "",
    status: "",
    started_at: null,
    ended_at: null,
    created_at: null,
    user_id: null,
    client_id: null,
    labels_variant: "default_v1",
    versions: [],

    // Documents
    principal_document_id: null,
    main_document: {
        id: null,
        type: "PRINCIPAL",
        document_type: null,
        title: "",
        language: "fr",
        industry: "",
        party_role: "",
        studied_party_name: "",
        jurisdiction: "",
        description: "",
        version: "",
        content_html: "",
        file_content: null,
        created_at: null,
        updated_at: null,
        user: null
    },
    amendments: [],
    reference_file_ids: [],
    reference_file: [],
    isGlobalLoading: false,
};

export const stageReducer = (state = initialStageState, action) => {
        switch (action.type) {
            case StageActionTypes.FLUSH_ANALYSE :
                const newListe = (state.criteriaListe || []).map(c => ({
                    ...c,
                    status: "not_executed",
                    executed_at: null
                }));
                return {
                    ...state,
                    criteriaListe: newListe
                };
            case StageActionTypes.SET_CRITERIA_STAGE:
                return {
                    ...state,
                    criteriaListe: action.payload,
                }
            case StageActionTypes.SET_CRITERION :
                return {
                    ...state,
                    selectedCriteria: state.selectedCriteria.filter(
                        id => id !== action.payload.id_criterion
                    ),
                    criteriaListe: state?.criteriaListe?.map(criterion =>
                        criterion.id === action.payload.id_criterion
                            ? {...criterion, ...action.payload.updates}
                            : criterion
                    ),

                };
            case StageActionTypes.SET_CRITERION_STATES: {
                const { ids, updates } = action.payload;

                // `ids` peut être: ["id1","id2"] ou [{id:"id1", ref:[]}, {id:"id2", ref:[]}]
                const idsOnly = (ids || []).map(x => (x && typeof x === "object" ? x.id : x));
                const idSet = new Set(idsOnly);

                return {
                    ...state,
                    criteriaListe: state?.criteriaListe?.map(c =>
                        idSet.has(c.id) ? { ...c, ...updates } : c
                    ),
                };
            }
            case StageActionTypes.SET_LABELS_VARIANT:
                return {
                    ...state,
                    labels_variant: normalizeLabelsVariant(action.payload),
                };

            case DocumentActionTypes.SET_GLOBAL_LOADING:
                return {...state, isGlobalLoading: action.payload};

            case StageActionTypes.FETCH_STAGE_DATA:
                const {analyse} = action.payload;
                return {
                    ...state,
                    criteriaListe: action.payload.criteria,
                    templates: action.payload.templates,

                    // Analyse metadata
                    id: analyse.id,
                    type_analyse: analyse.type_analyse,
                    title: analyse.title,
                    context: analyse.context,
                    status: analyse.status,
                    started_at: analyse.started_at,
                    ended_at: analyse.ended_at,
                    created_at: analyse.created_at,
                    user_id: analyse.user_id,
                    client_id: analyse.client_id,
                    labels_variant: normalizeLabelsVariant(analyse.labels_variant),
                    versions: analyse.versions || [],

                    // Keep the Document identity separate from the legacy shell,
                    // whose id is replaced by a DocumentVersion on ADD_VERSION.
                    principal_document_id: analyse.main_document?.id || null,

                    // Documents
                    main_document: {
                        ...state.main_document, // Garde les valeurs par défaut
                        ...analyse.main_document, // Écrase avec les données du backend
                        // Assurez-vous que les champs critiques sont toujours définis
                        type: "PRINCIPAL",
                        language: analyse.main_document?.language || "fr"
                    },
                    amendments: analyse.amendments || [],
                    reference_file_ids: analyse.reference_files?.map(f => f.id) || [],
                    reference_file: analyse.reference_files
                };

            case StageActionTypes.SET_SELECTED_TEMPLATE:
                return {
                    ...state,
                    selectedTemplate: action.payload,
                    selectedCriteria: normalizeExclusiveSystemSelection(
                        state.criteriaListe,
                        action.payload?.criteria || []
                    )
                };
            case StageActionTypes.REPLACE_CRITERIA_SELECTION: {
                const filterNonCompletedCriteria = (selections, criteriaList) => {
                    return selections.filter(selection => {
                        const crit = criteriaList.find(c => c.id === selection.id);
                        return crit?.status !== "completed";
                    });
                };

                const incomingSelection = (action.payload || []).map(selection => ({
                    id: selection?.id || selection,
                    ref: Array.isArray(selection?.ref) ? selection.ref : [],
                }));

                const filteredSelection = filterNonCompletedCriteria(
                    incomingSelection,
                    state.criteriaListe
                );

                return {
                    ...state,
                    selectedCriteria: normalizeExclusiveSystemSelection(
                        state.criteriaListe,
                        filteredSelection
                    )
                };
            }

            case StageActionTypes.TOGGLE_CRITERION_SELECTION: {
                const new_val = {id: action.payload, ref: []};
                const exists = state.selectedCriteria.some(item => item.id === action.payload);
                const toggledCriterion = (state.criteriaListe || []).find(c => c.id === action.payload);
                const isExclusiveToggle = isExclusiveSystemCriterion(toggledCriterion);

                const updated = exists
                    ? state.selectedCriteria.filter(item => item.id !== action.payload)
                    : isExclusiveToggle
                        ? [new_val]
                        : normalizeExclusiveSystemSelection(
                            state.criteriaListe,
                            [...state.selectedCriteria, new_val]
                        );

                return {
                    ...state,
                    selectedCriteria: updated
                };
            }

            case StageActionTypes.UPDATE_CRITERION_REFS: {
                const {id, refs} = action.payload;
                return {
                    ...state,
                    selectedCriteria: state.selectedCriteria.map(c =>
                        c.id === id ? {...c, ref: refs} : c
                    ),
                };
            }

            case StageActionTypes.CREATE_STAGE_TEMPLATE:
                return {
                    ...state,
                    templates: [...state.templates, action.payload],
                    selectedTemplate: action.payload,
                    selectedCriteria: normalizeExclusiveSystemSelection(
                        state.criteriaListe,
                        action.payload.criteria || []
                    )
                };

            case StageActionTypes.FETCH_TEMPLATES_SUCCESS:
                return {...state, templates: action.payload};

            case StageActionTypes.SET_TEMPLATE:
                return {...state, selectedTemplate: action.payload};

            case StageActionTypes.SET_STAGE_TYPE_ANALYSE:
                return {...state, type_analyse: action.payload};

            case StageActionTypes.SET_STAGE_TITLE:
                return {...state, title: action.payload};

            case DocumentActionTypes.SET_VERSION_TITLE:
                return {
                    ...state,
                    versions: (state.versions || []).map(version =>
                        String(version.id) === String(action.payload.id)
                            ? { ...version, title: action.payload.title }
                            : version
                    )
                };

            case StageActionTypes.SET_STAGE_CONTEXT:
                return {...state, context: action.payload};

            case StageActionTypes.SET_STAGE_STATUS:
                return {...state, status: action.payload};

            case StageActionTypes.SET_STAGE_MAIN_DOCUMENT:
                return {
                    ...state,
                    main_document: {
                        ...state.main_document,
                        ...action.payload,
                    },
                };
            case StageActionTypes.SET_DELETED_AMENDMENT:
                return {
                    ...state,
                    amendments: state.amendments.filter(am => am.id !== action.payload),
                }

            case StageActionTypes.INITIALISE_ANALYSIS:
                return {
                    ...state,
                    selectedTemplate: null,
                    selectedCriteria: [],
                    loading: false,
                }

            case StageActionTypes.ADD_VERSION:
                //Donne l'objet version + new_main_doc
                return {
                    ...state,
                    versions: [action.versionObject, ...state.versions],
                    main_document: {
                        ...action.versionObject // Mettre à jour les infos du document principal si nécessaire
                    }
                };

            case StageActionTypes.ADD_STAGE_AMENDMENT :
                return {
                    ...state,
                    amendments: [...state.amendments, action.payload],
                };

            case StageActionTypes.REMOVE_STAGE_AMENDMENT:
                return {
                    ...state,
                    amendments: state.amendments.filter(am => am.id !== action.payload),
                };

            case StageActionTypes.SET_STAGE_REFERENCE_FILES:
                // payload is an array of file IDs
                return {...state, reference_file_ids: action.payload};

            case StageActionTypes.ADD_REFERENCE_FILE_TO_STAGE:
                // Ajoute un fichier uploadé à la liste des reference_file
                return {
                    ...state,
                    reference_file: [
                        ...(state.reference_file || []).filter(file => file.id !== action.payload.id),
                        action.payload,
                    ],
                    reference_file_ids: Array.from(new Set([...(state.reference_file_ids || []), action.payload.id]))
                };

            case StageActionTypes.RESET_STAGE:
                return initialStageState;

            case StageActionTypes.ADD_CRITERION_TO_LIST:
                return {
                    ...state,
                    criteriaListe: [action.payload, ...state.criteriaListe]
                };

            case StageActionTypes.UPDATE_CRITERION_IN_LIST:
                return {
                    ...state,
                    criteriaListe: state.criteriaListe.map(c =>
                        c.id === action.payload.id ? { ...c, ...action.payload } : c
                    )
                };

            default:
                return state;
        }
    }
;
